import React, { useState, useMemo, useRef, type CSSProperties } from 'react';
import {
  collection, doc, runTransaction, serverTimestamp,
  type Transaction, type Firestore,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button, Dropdown, Modal, PageTabs, TotalsFigures } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesOpeningStock } from '../utils/ledger';
import {
  downloadImportWorkbook, loadWorkbook, readSheetRows, matchOption, cleanNum, type XlsxCol,
} from '../utils/excelImport';
import type { BusinessSettings, TransactionCategory } from '../types';

// Local calendar day — never toISOString() (UTC); see CLAUDE.md "Date-only strings".
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (s: string) => parseFloat(s) || 0;

function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
async function nextFreeCode(tx: Transaction, db: Firestore, cursor: { n: number }, reserved: Set<string>): Promise<string> {
  for (let i = 0; i < 300; i++) {
    cursor.n += 1;
    const cand = String(cursor.n);
    const id = normalizeCode(cand);
    if (reserved.has(id)) continue;
    const s = await tx.get(doc(db, 'itemCodes', id));
    if (!s.exists()) { reserved.add(id); return cand; }
  }
  throw new Error('Could not auto-generate a unique Code — enter one manually.');
}

// Same option lists Purchase's line-item grid uses.
const SHAPES     = ['Round', 'Princess', 'Oval', 'Cushion', 'Emerald', 'Pear', 'Marquise', 'Radiant', 'Asscher', 'Heart', 'Other'];
const COLORS     = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'Fancy'];
const CLARITIES  = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
const LABS       = ['GIA', 'IGI', 'HRD', 'AGS', 'GCAL', 'EGL', 'None'];
const CUT_GRADES = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];   // also Polish/Symmetry
const FLUORESCENCE_GRADES = ['None', 'Faint', 'Medium', 'Strong', 'Very Strong'];
// Text names are what the Excel file holds (a cell can't hold an icon);
// on screen the category is always the icon picker below.
const CATEGORIES: { value: TransactionCategory; label: string; short: string }[] = [
  { value: 'books-real',  label: 'By Books & Real',  short: 'By Books' },
  { value: 'cash-real',   label: 'Cash & Real',      short: 'Cash' },
  { value: 'books-dummy', label: 'By Books & Dummy', short: 'Dummy' },
];

// ── Category icon picker — Purchase.tsx's own CategoryPicker/CATEGORY_META,
// duplicated per this codebase's per-page convention (same as MemoIn/Sales).
// Icons only; each icon's title tooltip carries the full name.
function IconCatBooksReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.2 7.2l2 2 3.6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCatCashReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.6 3.6l6.8 6.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCatBooksDummy() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 1.5h4.5l2.5 2.5v8a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 1.5v2.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5 8.3c.3-.5 1-.6 1.4-.3.5.3.5.9 0 1.2-.6.3-.6.9 0 1.2.4.3 1.1.2 1.4-.3M6.6 7v5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>;
}
function IconChevronDownSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
const CATEGORY_META: Record<TransactionCategory, { label: string; color: string; icon: React.ReactNode }> = {
  'books-real':  { label: 'By Books & Real',  color: 'var(--success)', icon: <IconCatBooksReal /> },
  'cash-real':   { label: 'Cash & Real',      color: 'var(--danger)',  icon: <IconCatCashReal /> },
  'books-dummy': { label: 'By Books & Dummy', color: 'var(--text-2)',  icon: <IconCatBooksDummy /> },
};
function CategoryPicker({ value, onChange }: { value: TransactionCategory | ''; onChange: (v: TransactionCategory | '') => void }) {
  const current = value ? CATEGORY_META[value] : null;
  return (
    <Dropdown
      panelClassName="icon-only"
      trigger={() => (
        <button type="button" title={current?.label ?? 'Select category'} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
          height: 22, padding: '0 6px', borderRadius: 5, boxSizing: 'border-box', flexShrink: 0,
          border: `1.5px solid ${current ? current.color : 'var(--border)'}`,
          background: current ? `color-mix(in srgb, ${current.color} 14%, transparent)` : 'var(--surface)',
          color: current ? current.color : 'var(--text-3)',
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          {current ? current.icon : <IconCatBooksReal />}
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
        </button>
      )}
    >
      {CATEGORIES.map(({ value: key }) => {
        const meta = CATEGORY_META[key];
        return (
          <button key={key} type="button" className="dropdown-item" title={meta.label}
            style={{ color: meta.color, justifyContent: 'center' }} onClick={() => onChange(key)}>
            {meta.icon}
          </button>
        );
      })}
    </Dropdown>
  );
}

// ── Grid styles — copied from Purchase.tsx's Excel-style line-item grid
// (GTH/GTD/GTD_TEXT there) so both grids read identically; the borders and
// edge-to-edge inputs come from the global .excel-grid-table / .grid-cell CSS.
const GTD: CSSProperties = { padding: 0, verticalAlign: 'middle' };
const GTD_TEXT: CSSProperties = { ...GTD, padding: '6px 8px', fontSize: 11 };
const GTH: CSSProperties = {
  ...GTD_TEXT, fontSize: 9.5, fontWeight: 700, color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 2,
};
const MISSING_BG = 'color-mix(in srgb, var(--danger) 12%, transparent)';
const ADD_ROW_H = 36;

function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M3 4.5h10M6.5 4V2.8h3V4M4.2 4.5l.6 8.2a1 1 0 0 0 1 .9h4.4a1 1 0 0 0 1-.9l.6-8.2M6.6 7v4M9.4 7v4"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconImport() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M8 2v8M5 7l3 3 3-3M3 12.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconUploadFile() {
  return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M12 17v-6" /><path d="M9.5 13.5 12 11l2.5 2.5" />
  </svg>;
}

// ── Row model ────────────────────────────────────────────────────────────────
// Opening stock is goods you already own — no supplier, purchase date,
// terms or brokers per stone (it's booked as one batch against opening
// equity and from then on just lives in Inventory). Everything that
// describes the stone itself is here, the same fields a purchased stone
// carries.
type RowType = 'loose' | 'certified';

interface Row {
  _key:     string;
  itemType: RowType;
  category: TransactionCategory | '';
  code: string; sku: string; description: string;
  shape: string; size: string; subSize: string; carat: string;
  color: string; clarity: string;
  lab: string; certificateNo: string;
  cut: string; polish: string; symmetry: string; fluorescence: string;
  measLength: string; measWidth: string; measDepth: string;
  // Certified: List Price/ct + Discount % ↔ Cost/ct stay in sync, same rule
  // as Purchase's resolveCertPrice ('discount' = List × (1 + Disc/100) drives
  // Cost/ct; 'price' = Cost/ct typed directly, Discount % derived).
  listPrice: string; discount: string;
  priceMode: 'discount' | 'price' | undefined;
  // Cost/ct — a per-carat rate; the row's total cost (carat × cost) is derived.
  cost: string;
}

let _rowKey = 0;
function newRow(itemType: RowType): Row {
  return {
    _key: String(++_rowKey), itemType, category: 'books-real',
    code: '', sku: '', description: '', shape: '', size: '', subSize: '', carat: '',
    color: '', clarity: '', lab: '', certificateNo: '',
    cut: '', polish: '', symmetry: '', fluorescence: '', measLength: '', measWidth: '', measDepth: '',
    listPrice: '', discount: '', priceMode: undefined, cost: '',
  };
}

function resolveCertCost(r: Row): Row {
  if (r.itemType !== 'certified') return r;
  const list = num(r.listPrice);
  if (r.priceMode === 'price') {
    if (list <= 0) return r;
    return { ...r, discount: String(+(((num(r.cost) - list) / list) * 100).toFixed(4)) };
  }
  return list > 0 ? { ...r, cost: String(+(list * (1 + num(r.discount) / 100)).toFixed(4)) } : r;
}

const rowAmount = (r: Row) => num(r.carat) * num(r.cost);

// ── Column spec — one per tab. Drives BOTH the on-screen grid and the Excel
// sample/import (same headers, same order), so the two can't drift apart. ──
type Field = Exclude<keyof Row, '_key' | 'itemType' | 'priceMode'> | 'totalAmount';
type ListKey = 'category' | 'shape' | 'looseColor' | 'looseClarity' | 'certColor' | 'certClarity' | 'lab' | 'grade' | 'fluor';
interface Col extends XlsxCol<Field> {
  list?:     ListKey;
  required?: boolean;
  placeholder?: string;
}

const totalFormula = (r: number, col: (f: Field) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('cost')}${r}=""),"",ROUND(${col('carat')}${r}*${col('cost')}${r},2))`;

const LOOSE_COLS: Col[] = [
  { header: 'Category',     field: 'category',    width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'Code',         field: 'code',        width: 10, placeholder: 'Auto', note: 'Optional — blank = auto-numbered' },
  { header: 'SKU',          field: 'sku',         width: 10, required: true, aliases: ['item no', 'item number'], note: 'Required' },
  { header: 'Description',  field: 'description', width: 36 },
  { header: 'Shape',        field: 'shape',       width: 12, list: 'shape', required: true, note: 'Required' },
  { header: 'Size',         field: 'size',        width: 12, placeholder: '+2' },
  { header: 'Sub Size',     field: 'subSize',     width: 10, placeholder: '+1.5', aliases: ['subsize'] },
  { header: 'Carat',        field: 'carat',       width: 9,  numeric: true, required: true, placeholder: '0.00', note: 'Required' },
  { header: 'Cost/ct',      field: 'cost',        width: 11, numeric: true, required: true, placeholder: '0.00', aliases: ['cost per ct', 'price/ct', 'price per ct', 'price'], note: 'Required — what each carat cost you' },
  { header: 'Color',        field: 'color',       width: 9,  list: 'looseColor', required: true, note: 'Required' },
  { header: 'Clarity',      field: 'clarity',     width: 9,  list: 'looseClarity', required: true, note: 'Required' },
  { header: 'Total Amount', field: 'totalAmount', width: 13, numeric: true, aliases: ['amount', 'total cost', 'cost'], formula: totalFormula },
];

const CERT_COLS: Col[] = [
  { header: 'Category',      field: 'category',      width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'Code',          field: 'code',          width: 10, placeholder: 'Auto', note: 'Optional — blank = auto-numbered' },
  { header: 'Description',   field: 'description',   width: 36 },
  { header: 'Lab',           field: 'lab',           width: 9,  list: 'lab', required: true, note: 'Required' },
  { header: 'Cert No',       field: 'certificateNo', width: 14, aliases: ['certificate no', 'cert'], note: 'Required unless Lab = None' },
  { header: 'Shape',         field: 'shape',         width: 12, list: 'shape', required: true, note: 'Required' },
  { header: 'Carat',         field: 'carat',         width: 9,  numeric: true, required: true, placeholder: '0.00', note: 'Required' },
  { header: 'List Price/ct', field: 'listPrice',     width: 13, numeric: true, aliases: ['list price', 'list price per ct'] },
  { header: 'Discount %',    field: 'discount',      width: 11, numeric: true, aliases: ['disc'], note: 'Negative = discount, e.g. -35' },
  { header: 'Cost/ct',       field: 'cost',          width: 11, numeric: true, required: true, placeholder: '0.00', aliases: ['cost per ct', 'price/ct', 'price per ct', 'price'], note: 'Required — or leave blank and give List Price + Discount %' },
  { header: 'Total Amount',  field: 'totalAmount',   width: 13, numeric: true, aliases: ['amount', 'total cost', 'cost'], formula: totalFormula },
  { header: 'Color',         field: 'color',         width: 9,  list: 'certColor', required: true, note: 'Required' },
  { header: 'Clarity',       field: 'clarity',       width: 9,  list: 'certClarity', required: true, note: 'Required' },
  { header: 'Cut',           field: 'cut',           width: 11, list: 'grade' },
  { header: 'Polish',        field: 'polish',        width: 11, list: 'grade' },
  { header: 'Symmetry',      field: 'symmetry',      width: 11, list: 'grade' },
  { header: 'Fluorescence',  field: 'fluorescence',  width: 13, list: 'fluor', aliases: ['fluor'] },
  { header: 'Length',        field: 'measLength',    width: 9,  numeric: true, aliases: ['l'] },
  { header: 'Width',         field: 'measWidth',     width: 9,  numeric: true, aliases: ['w'] },
  { header: 'Depth',         field: 'measDepth',     width: 9,  numeric: true, aliases: ['d'] },
];

const colsFor = (t: RowType) => (t === 'loose' ? LOOSE_COLS : CERT_COLS);

function optionLists(settings: BusinessSettings | null): Record<ListKey, string[]> {
  return {
    category:     CATEGORIES.map(c => c.label),
    shape:        SHAPES,
    // Loose and certified stones use different Color/Clarity scales — same
    // as Purchase: loose follows Settings' custom loose options, certified
    // always the standard grading scale.
    looseColor:   settings?.looseColorOptions?.length ? settings.looseColorOptions : COLORS,
    looseClarity: settings?.looseClarityOptions?.length ? settings.looseClarityOptions : CLARITIES,
    certColor:    COLORS,
    certClarity:  CLARITIES,
    lab:          LABS,
    grade:        CUT_GRADES,
    fluor:        FLUORESCENCE_GRADES,
  };
}

// A row counts as "started" the moment anything is typed into it — an
// untouched blank row is simply ignored, not required to be complete.
// (Category starts pre-filled, so it doesn't count.)
function rowStarted(r: Row): boolean {
  return colsFor(r.itemType).some(c => !c.formula && c.field !== 'category' && String(r[c.field as keyof Row] ?? '').trim());
}
function cellMissing(r: Row, c: Col): boolean {
  if (c.field === 'certificateNo') return r.lab !== 'None' && !r.certificateNo.trim();
  if (!c.required) return false;
  const v = String(r[c.field as keyof Row] ?? '').trim();
  return !v || ((c.field === 'carat' || c.field === 'cost') && num(v) <= 0);
}
function missingCount(r: Row): number {
  return rowStarted(r) ? colsFor(r.itemType).filter(c => cellMissing(r, c)).length : 0;
}

// ── Excel import ─────────────────────────────────────────────────────────────
const LOOSE_SHEET = 'Loose Polished';
const CERT_SHEET  = 'Certified Polished';

async function downloadOpeningStockSample(settings: BusinessSettings | null) {
  const s = (o: Partial<Record<Field, string | number>>) => o;
  await downloadImportWorkbook<Field>({
    fileName: 'opening-stock-import-sample.xlsx',
    lists: optionLists(settings),
    sheets: [
      { name: LOOSE_SHEET, cols: LOOSE_COLS, tabArgb: 'FF10B981', sample: [
        s({ category: 'By Books & Real', sku: 'PARCEL-A', description: 'Melee parcel', shape: 'Round', size: '1.00-1.20', subSize: '+2', carat: 5.2, cost: 810, color: 'H', clarity: 'SI1' }),
        s({ category: 'By Books & Real', sku: 'PARCEL-B', shape: 'Oval', size: '0.80-0.90', carat: 3.1, cost: 600, color: 'I', clarity: 'SI2' }),
      ] },
      { name: CERT_SHEET, cols: CERT_COLS, tabArgb: 'FF4F46E5', sample: [
        s({ category: 'By Books & Real', lab: 'GIA', certificateNo: '1234567890', shape: 'Round', carat: 1.01, listPrice: 12000, discount: -40, color: 'F', clarity: 'VS1', cut: 'Excellent', polish: 'Excellent', symmetry: 'Excellent', fluorescence: 'None', measLength: 6.45, measWidth: 6.48, measDepth: 3.99 }),
        s({ category: 'By Books & Real', lab: 'None', shape: 'Princess', carat: 0.9, cost: 4900, color: 'G', clarity: 'VS2' }),
      ] },
    ],
    readme: [
      'Opening Stock import — the stones you already own',
      '',
      `• Put loose (parcel) goods on the "${LOOSE_SHEET}" sheet and certified stones on the "${CERT_SHEET}" sheet. Leave a sheet empty if you have none of that kind.`,
      '• Opening stock has no supplier, purchase date or terms — it is loaded as one batch and then lives in Inventory. The batch date and notes are set on the Opening Stock page.',
      '• Replace the example rows on each sheet with your own (delete them if not needed).',
      '• Row 1 headers must stay as they are. Column order can change, and optional columns can be deleted.',
      '• Cost/ct = what each carat cost you. The grey Total Amount column is calculated for you (if you only have a total, put it there and leave Cost/ct blank).',
      '• Certified: give either Cost/ct, or List Price/ct + Discount % (negative = discount, e.g. -40).',
      '• Code: leave blank to auto-number, exactly like adding stock by hand.',
      '• After import every row appears in the Opening Stock grid, where you can check and fix it before loading.',
    ],
  });
}

async function parseOpeningStockXlsx(data: ArrayBuffer, settings: BusinessSettings | null): Promise<{ rows: Row[]; warnings: string[] }> {
  const wb = await loadWorkbook(data);
  const lists = optionLists(settings);
  const warnings: string[] = [];
  const rows: Row[] = [];

  const read = (sheetName: string, type: RowType, match: (n: string) => boolean) => {
    for (const { sheet, rowNo, text } of readSheetRows(wb, sheetName, match, colsFor(type), warnings) ?? []) {
      const where = `${sheet} row ${rowNo}`;
      const get = (f: Field) => text[f] ?? '';
      const n   = (f: Field) => cleanNum(get(f));
      const opt = (f: Field, options: readonly string[], label: string) => matchOption(get(f), options, label, where, warnings);
      const isCert = type === 'certified';
      const r = newRow(type);

      const cat = get('category').toLowerCase();
      if (cat) {
        const hit = CATEGORIES.find(c => c.value === cat || c.label.toLowerCase() === cat || c.short.toLowerCase() === cat);
        if (hit) r.category = hit.value;
        else warnings.push(`${where}: Category "${get('category')}" not recognised — set to By Books & Real.`);
      }
      r.code        = get('code');
      r.description = get('description');
      r.shape       = opt('shape', SHAPES, 'Shape');
      r.carat       = n('carat');
      r.color       = opt('color',   isCert ? lists.certColor   : lists.looseColor,   'Color');
      r.clarity     = opt('clarity', isCert ? lists.certClarity : lists.looseClarity, 'Clarity');
      // Cost/ct, falling back to Total Amount ÷ Carat when only a total was given.
      const ct = num(r.carat), total = n('totalAmount');
      r.cost = n('cost') || (total && ct > 0 ? String(+(num(total) / ct).toFixed(4)) : '');

      if (isCert) {
        r.lab           = opt('lab', LABS, 'Lab');
        r.certificateNo = get('certificateNo');
        r.cut           = opt('cut', CUT_GRADES, 'Cut');
        r.polish        = opt('polish', CUT_GRADES, 'Polish');
        r.symmetry      = opt('symmetry', CUT_GRADES, 'Symmetry');
        r.fluorescence  = opt('fluorescence', FLUORESCENCE_GRADES, 'Fluorescence');
        r.measLength    = n('measLength');
        r.measWidth     = n('measWidth');
        r.measDepth     = n('measDepth');
        r.listPrice     = n('listPrice');
        r.discount      = n('discount');
        r.priceMode     = num(r.listPrice) > 0 && r.discount !== '' ? 'discount' : (r.cost ? 'price' : undefined);
        rows.push(resolveCertCost(r));
      } else {
        r.size    = get('size');
        r.subSize = get('subSize');
        r.sku     = get('sku');
        rows.push(r);
      }
    }
  };
  read(LOOSE_SHEET, 'loose', n => n.includes('loose'));
  read(CERT_SHEET, 'certified', n => n.includes('cert'));
  if (!wb.worksheets.some(w => /loose|cert/i.test(w.name))) {
    warnings.push(`No "${LOOSE_SHEET}" or "${CERT_SHEET}" sheet found — start from the sample file.`);
  }
  return { rows, warnings };
}

// Pick the file, see what was found, then "Add to Opening Stock" drops the
// rows into the page's own grid — that grid IS the preview (same columns,
// editable, red cells for anything missing), and the page's own "Load
// Opening Stock" stays the single save path.
function ImportOpeningStockModal({
  open, onClose, settings, onRows,
}: {
  open:     boolean;
  onClose:  () => void;
  settings: BusinessSettings | null;
  onRows:   (rows: Row[]) => void;
}) {
  const money = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');
  const [fileName, setFileName] = useState('');
  const [parsed,   setParsed]   = useState<{ rows: Row[]; warnings: string[] } | null>(null);
  const [busy,     setBusy]     = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error,    setError]    = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // Reset is handled by remounting (key={} at the call site).

  async function readFile(f: File) {
    setError('');
    if (!/\.xlsx$/i.test(f.name)) { setError('Please choose an Excel (.xlsx) file — start from the sample file.'); return; }
    setBusy(true);
    try {
      const res = await parseOpeningStockXlsx(await f.arrayBuffer(), settings);
      setFileName(f.name);
      setParsed(res);
      if (res.rows.length === 0) setError('No stones found in that file.');
    } catch {
      setError('Could not read that file. Make sure it is an .xlsx saved from Excel, Numbers or Google Sheets.');
    }
    setBusy(false);
  }

  const rows = parsed?.rows ?? [];
  const nLoose = rows.filter(r => r.itemType === 'loose').length;
  const toFix  = rows.reduce((s, r) => s + missingCount(r), 0);
  const stats: [string, string][] = [
    ['Loose rows', String(nLoose)],
    ['Certified rows', String(rows.length - nLoose)],
    ['Total carat', `${rows.reduce((s, r) => s + num(r.carat), 0).toFixed(2)} ct`],
    ['Total cost', money(rows.reduce((s, r) => s + rowAmount(r), 0))],
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Opening Stock"
      subtitle="Excel file with a Loose Polished sheet and a Certified Polished sheet"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={rows.length === 0} onClick={() => onRows(rows)}>
            Add {rows.length || ''} Row{rows.length === 1 ? '' : 's'} to Opening Stock
          </Button>
        </>
      }
    >
      <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) readFile(f); }} />

      <div
        role="button" tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
        style={{
          border: `1.5px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
          background: dragOver ? 'var(--primary-light)' : 'var(--surface-2)',
          borderRadius: 'var(--r-sm)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer',
          color: 'var(--text-3)', transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <IconUploadFile />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginTop: 6 }}>
          {busy ? 'Reading…' : fileName || 'Choose or drop an Excel file'}
        </div>
        <div style={{ fontSize: 11.5, marginTop: 2 }}>
          {fileName ? 'Click to choose a different file' : '.xlsx · loose and certified stones on two sheets'}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button type="button" className="line-item-link-btn"
          onClick={() => downloadOpeningStockSample(settings).catch(() => setError('Could not create the sample file.'))}>
          Download Sample File
        </button>
      </div>

      {rows.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 14 }}>
            {stats.map(([label, value]) => (
              <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 10px' }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--font-num)', marginTop: 2 }}>{value}</div>
              </div>
            ))}
          </div>
          <p className="form-hint" style={{ marginTop: 10, marginBottom: 0 }}>
            The rows go into the Opening Stock grid, where you can check and edit every one before loading.
            {toFix > 0 && <> <strong style={{ color: 'var(--danger)' }}>{toFix} cell{toFix === 1 ? '' : 's'}</strong> still need filling — they'll show red.</>}
          </p>
        </>
      )}

      {parsed && parsed.warnings.length > 0 && (
        <div className="msg msg-info" style={{ marginTop: 12, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto' }}>
          {parsed.warnings.join('\n')}
        </div>
      )}

      {error && <div className="msg msg-error" style={{ marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function OpeningStockPage() {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const lists = useMemo(() => optionLists(settings), [settings]);

  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>(() => [newRow('loose'), newRow('certified')]);
  const [tab, setTab] = useState<RowType>('loose');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const tabRows = rows.filter(r => r.itemType === tab);
  const cols    = colsFor(tab);
  const inputCols = cols.filter(c => !c.formula && c.field !== 'category');
  const nav = useGridKeyNav(tabRows.length, inputCols.length);

  function update(key: string, field: Field, value: string) {
    setRows(rs => rs.map(r => {
      if (r._key !== key) return r;
      if (field === 'listPrice') return resolveCertCost({ ...r, listPrice: value });
      if (field === 'discount')  return resolveCertCost({ ...r, discount: value, priceMode: 'discount' });
      if (field === 'cost' && r.itemType === 'certified') return resolveCertCost({ ...r, cost: value, priceMode: 'price' });
      return { ...r, [field]: value };
    }));
  }
  function addRow() {
    const idx = tabRows.length;
    setRows(rs => [...rs, newRow(tab)]);
    setTimeout(() => nav.focusCell(idx, 0), 0);
  }
  function removeRow(key: string) { setRows(rs => rs.filter(r => r._key !== key)); }

  // Imported rows replace any untouched blank rows, then append — rows the
  // user already typed are kept.
  function loadImportedRows(imported: Row[]) {
    setRows(prev => [...prev.filter(rowStarted), ...imported]);
    setTab(imported.some(r => r.itemType === 'loose') ? 'loose' : 'certified');
    setImportOpen(false);
    setDone(`${imported.length} row${imported.length === 1 ? '' : 's'} added from the file — check them below, then Load Opening Stock.`);
    setError('');
  }

  // `entered` = every row actually started; `valid` = the complete ones.
  // Load stays blocked while any started row is incomplete, since nothing
  // here can be edited once it's loaded into inventory.
  const entered = useMemo(() => rows.filter(rowStarted), [rows]);
  const valid   = useMemo(() => entered.filter(r => missingCount(r) === 0), [entered]);
  const looseToFix = rows.filter(r => r.itemType === 'loose').reduce((s, r) => s + missingCount(r), 0);
  const certToFix  = rows.filter(r => r.itemType === 'certified').reduce((s, r) => s + missingCount(r), 0);
  const nLoose = rows.filter(r => r.itemType === 'loose' && rowStarted(r)).length;
  const nCert  = rows.filter(r => r.itemType === 'certified' && rowStarted(r)).length;

  const dupeCode = useMemo(() => {
    const codes = entered.filter(r => r.code.trim()).map(r => normalizeCode(r.code));
    return codes.find((c, i) => codes.indexOf(c) !== i) ?? null;
  }, [entered]);
  const blocker = entered.length === 0 ? 'Add at least one stone.'
    : looseToFix + certToFix > 0 ? `Fill the ${looseToFix + certToFix} red cell${looseToFix + certToFix === 1 ? '' : 's'} first${looseToFix && certToFix ? ' (on both tabs)' : looseToFix ? ' (Loose tab)' : ' (Certified tab)'} — stock can't be edited once it's loaded.`
    : dupeCode ? `Two rows use the same Code (${dupeCode}). Each stone needs its own Code.`
    : null;

  const totalCarat = round2(valid.reduce((s, r) => s + num(r.carat), 0));
  const totalCost  = round2(valid.reduce((s, r) => s + rowAmount(r), 0));
  const tabCarat   = round2(tabRows.filter(rowStarted).reduce((s, r) => s + num(r.carat), 0));
  const tabCost    = round2(tabRows.filter(rowStarted).reduce((s, r) => s + rowAmount(r), 0));

  async function save() {
    if (blocker) return setError(blocker);
    setSaving(true); setError(''); setDone('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = (settings.openingStockPrefix || 'OS').toUpperCase();
    const batchRef = doc(collection(db, 'openingStock'));
    const itemRefs = valid.map(() => doc(collection(db, 'inventory')));
    const counterRef = doc(db, 'counters', business.id);
    const typedCodeIds = [...new Set(valid.filter(r => r.code.trim()).map(r => normalizeCode(r.code)))];

    try {
      await runTransaction(db, async tx => {
        const cs = await tx.get(counterRef);
        // collision-check typed codes
        for (const id of typedCodeIds) {
          const s = await tx.get(doc(db, 'itemCodes', id));
          if (s.exists()) throw new Error(`Code "${id}" is already used by another item.`);
        }
        const storedCursor = cs.exists() ? (cs.data().itemCode ?? settings.itemCodeStart ?? 0) : (settings.itemCodeStart ?? 0);
        const cursor = { n: storedCursor };
        const reserved = new Set(typedCodeIds);
        const osN = (cs.exists() ? (cs.data().openingStock ?? 0) : 0) + 1;

        // resolve a final code per row
        const codes: string[] = [];
        for (const r of valid) {
          codes.push(r.code.trim() ? r.code.trim() : await nextFreeCode(tx, db, cursor, reserved));
        }
        for (const c of codes) {
          const n = Number(c);
          if (Number.isFinite(n) && n > cursor.n) cursor.n = n;
        }

        // ── Writes ──
        tx.set(counterRef, stripUndef({ itemCode: cursor.n, openingStock: osN }), { merge: true });

        valid.forEach((r, i) => {
          const cost = round2(rowAmount(r));
          const code = codes[i];
          const codeId = normalizeCode(code);
          const isCert = r.itemType === 'certified';
          const opt = (v: string) => v.trim() || undefined;
          tx.set(itemRefs[i], stripUndef({
            businessId: business.id,
            purchaseId: 'opening', purchaseNo: prefix, lineItemId: '',
            type: isCert ? 'polished-certified' : 'polished-loose',
            status: 'in-stock',
            provenance: 'opening',
            category: r.category || undefined,
            shape: r.shape.trim(), carat: round2(num(r.carat)),
            color: r.color.trim(), clarity: r.clarity.trim(),
            lab:           isCert ? opt(r.lab) : undefined,
            certificateNo: isCert ? opt(r.certificateNo) : undefined,
            cut:           isCert ? opt(r.cut) : undefined,
            polish:        isCert ? opt(r.polish) : undefined,
            symmetry:      isCert ? opt(r.symmetry) : undefined,
            fluorescence:  isCert ? opt(r.fluorescence) : undefined,
            measLength:    isCert ? opt(r.measLength) : undefined,
            measWidth:     isCert ? opt(r.measWidth) : undefined,
            measDepth:     isCert ? opt(r.measDepth) : undefined,
            size:          !isCert ? opt(r.size) : undefined,
            subSize:       !isCert ? opt(r.subSize) : undefined,
            sku:           !isCert ? r.sku.trim() : undefined,
            notes:         opt(r.description),
            description:   opt(r.description),
            code, codeId,
            costUSD: cost, costingUSD: cost,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          }));
          tx.set(doc(db, 'itemCodes', codeId), stripUndef({
            businessId: business.id, code, inventoryItemId: itemRefs[i].id,
            createdAt: serverTimestamp(), createdBy: user!.id,
          }));
        });

        tx.set(batchRef, stripUndef({
          businessId: business.id,
          batchNo: `${prefix}-${new Date().getFullYear()}-${String(osN).padStart(4, '0')}`,
          date, itemCount: valid.length, totalCarat, totalCostUSD: totalCost,
          inventoryItemIds: itemRefs.map(r => r.id),
          notes: notes.trim() || undefined,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'opening_stock', sourceId: batchRef.id,
          sourceCollection: 'openingStock', date, currency: settings.currency, createdBy: user!.id,
          note: 'Opening stock loaded',
          lines: linesOpeningStock(totalCost),
        }), entryId('opening_stock', batchRef.id));
      });
      setDone(`Loaded ${valid.length} item${valid.length === 1 ? '' : 's'} (${money(totalCost)}) into stock.`);
      setRows([newRow('loose'), newRow('certified')]);
      setNotes('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save opening stock.');
    }
    setSaving(false);
  }

  function renderCell(r: Row, c: Col, idx: number) {
    if (c.formula) {
      const v = rowAmount(r);
      return <td key={c.field} style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', textAlign: 'right', color: 'var(--text-2)', background: 'var(--surface-2)', whiteSpace: 'nowrap' }}>
        {v > 0 ? money(v) : <span style={{ opacity: 0.4 }}>—</span>}
      </td>;
    }
    if (c.field === 'category') {
      return <td key={c.field} style={GTD_TEXT}>
        <CategoryPicker value={r.category} onChange={v => update(r._key, 'category', v)} />
      </td>;
    }
    const colIdx = inputCols.indexOf(c);
    const value  = String(r[c.field as keyof Row] ?? '');
    const tdStyle: CSSProperties = { ...GTD, minWidth: c.width * 7, background: rowStarted(r) && cellMissing(r, c) ? MISSING_BG : undefined };
    const cellProps = { ref: nav.cellRef(idx, colIdx), onKeyDown: nav.onCellKeyDown(idx, colIdx) };
    const disabled = (c.field === 'certificateNo' && r.lab === 'None')
      || ((c.field === 'listPrice' || c.field === 'discount') && r.lab === 'None');

    if (c.list) {
      const opts = c.list === 'category'
        ? CATEGORIES.map(o => ({ value: o.value, label: o.label }))
        : lists[c.list].map(o => ({ value: o, label: o }));
      // A non-standard value from an imported file stays selectable instead of silently vanishing.
      if (value && !opts.some(o => o.value === value)) opts.push({ value, label: `${value} (from file)` });
      return <td key={c.field} style={tdStyle}>
        <select {...cellProps} className="grid-cell grid-cell-select" value={value} onChange={e => update(r._key, c.field, e.target.value)}>
          <option value="">{c.header}</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>;
    }
    return <td key={c.field} style={tdStyle}>
      <input {...cellProps}
        className={`grid-cell${c.numeric ? ' no-spinner' : ''}`}
        type={c.numeric ? 'number' : 'text'} step="any" min={c.field === 'discount' ? undefined : '0'}
        style={c.numeric ? { textAlign: 'right', fontFamily: 'var(--font-num)' } : c.field === 'code' || c.field === 'sku' || c.field === 'certificateNo' ? { fontFamily: 'var(--font-num)' } : undefined}
        placeholder={disabled ? 'n/a' : c.placeholder ?? ''}
        value={value} disabled={disabled}
        onChange={e => update(r._key, c.field, e.target.value)}
      />
    </td>;
  }

  return (
    <AppLayout
      title="Opening Stock"
      subtitle="Load stock you already had before you started using the app — one entry, booked against opening equity."
      toolbar={<>
        {/* The batch details sit in the header: the count date and an
            optional note, each a labelled box. */}
        <label className="tb-field" title="The day this stock was counted">
          <span className="tb-field-label">Stock date</span>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </label>
        <label className="tb-field tb-field-grow">
          <span className="tb-field-label">Notes</span>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional, e.g. count as of go live" />
        </label>
      </>}
      headerActions={<>
        <div className="tb-group">
          <button type="button" className="tb-group-btn" title="Import from Excel" aria-label="Import from Excel" onClick={() => setImportOpen(true)}>
            <IconImport />
          </button>
        </div>
        <Button variant="primary" size="sm" onClick={save} loading={saving} disabled={!!blocker}
          title={blocker || undefined} style={{ height: 34, boxSizing: 'border-box' }}>
          Load Opening Stock
        </Button>
      </>}
    >
      {error && <div className="msg msg-error">{error}</div>}
      {done && <div className="msg msg-info">{done}</div>}

      {/* ── Loose / Certified — tab-switched Excel-style grids, same as Add Purchase Invoice ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <PageTabs
          className="purchase-items-tabs"
          tabs={[
            { key: 'loose',     label: `Loose Polished (${nLoose})${looseToFix ? ` · ${looseToFix} to fix` : ''}` },
            { key: 'certified', label: `Certified Polished (${nCert})${certToFix ? ` · ${certToFix} to fix` : ''}` },
          ]}
          active={tab}
          onChange={k => setTab(k as RowType)}
        />
        <p className="form-hint" style={{ margin: 0 }}>Blank Codes are auto-numbered on save · batch number assigned on save</p>
      </div>

      <div style={{
        border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)',
        overflow: 'auto', maxHeight: 'calc(100vh - 400px)', minHeight: 240, marginBottom: 14,
        overscrollBehavior: 'contain',
      }}>
        <table className="excel-grid-table">
          <thead>
            <tr>
              <th style={GTH}>#</th>
              {cols.map(c => (
                <th key={c.field} style={{ ...GTH, minWidth: c.field === 'category' ? undefined : c.width * 7, textAlign: c.numeric ? 'right' : 'left' }}>
                  {c.field === 'category' ? 'Cat.' : c.header === 'Cost/ct' ? `Cost/ct (${settings.currency})` : c.header}
                  {c.required || c.field === 'certificateNo' ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
                </th>
              ))}
              <th className="excel-grid-remove-col" style={GTH} />
            </tr>
          </thead>
          <tbody>
            {tabRows.map((r, idx) => {
              const ready = rowStarted(r) && missingCount(r) === 0;
              return (
                <tr key={r._key}>
                  <td style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', color: ready ? 'var(--primary)' : 'var(--text-3)', fontWeight: ready ? 700 : 400 }}>{idx + 1}</td>
                  {cols.map(c => renderCell(r, c, idx))}
                  <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                    <button type="button" className="line-item-remove" title="Remove row" onClick={() => removeRow(r._key)}>
                      <IconTrash />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Add Row — a normal last row, same shape as Purchase's grid */}
            <tr>
              <td colSpan={cols.length + 2} style={{ padding: 0, background: 'var(--primary-light)' }}>
                <button type="button" onClick={addRow} style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                  height: ADD_ROW_H, padding: '0 12px',
                  fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                  <IconPlus /> Add {tab === 'loose' ? 'Loose' : 'Certified'} Row
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...GTD_TEXT, position: 'sticky', bottom: 0, background: 'var(--surface-2)', fontWeight: 700 }}>Σ</td>
              {cols.map(c => (
                <td key={c.field} style={{
                  ...GTD_TEXT, position: 'sticky', bottom: 0, background: 'var(--surface-2)',
                  fontFamily: 'var(--font-num)', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-1)',
                }}>
                  {c.field === 'carat' ? tabCarat.toFixed(2) : c.field === 'totalAmount' ? money(tabCost) : ''}
                </td>
              ))}
              <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, position: 'sticky', bottom: 0, background: 'var(--surface-2)' }} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Totals bar (Load Opening Stock is in the header) ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)',
        padding: '10px 14px',
      }}>
        <TotalsFigures figures={[
          { label: 'Ready', value: String(valid.length) },
          { label: 'Carat', value: `${totalCarat.toFixed(2)} ct` },
          { label: 'Cost', value: money(totalCost), strong: true },
        ]} />
        {blocker && entered.length > 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginLeft: 'auto' }}>{blocker}</span>
        )}
      </div>

      <ImportOpeningStockModal
        key={importOpen ? 'imp-open' : 'imp-closed'}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        settings={settings}
        onRows={loadImportedRows}
      />
    </AppLayout>
  );
}
